import { useState, useRef, useEffect, useCallback } from "react";
import { ArrowsCounterClockwise, ArrowsLeftRight, Spinner } from "@phosphor-icons/react";
import useComparisonImages from "./useComparisonImages";

/**
 * Red/Blue overlay viewer using Canvas 2D compositing.
 * Reference is tinted red, latest is tinted blue. Overlap appears purple.
 */
export default function OverlayViewer({ sessionId }) {
  const { latestImage, referenceImage, loading, error } = useComparisonImages(sessionId);
  const [opacity, setOpacity] = useState(50);
  const [swapped, setSwapped] = useState(false);
  const canvasRef = useRef(null);
  const refImgRef = useRef(null);
  const latImgRef = useRef(null);
  const [imagesLoaded, setImagesLoaded] = useState({ ref: false, lat: false });

  // Load images into Image objects for canvas drawing
  useEffect(() => {
    if (!referenceImage) return;
    const img = new Image();
    img.onload = () => {
      refImgRef.current = img;
      setImagesLoaded((prev) => ({ ...prev, ref: true }));
    };
    img.src = `data:image/jpeg;base64,${referenceImage}`;
  }, [referenceImage]);

  useEffect(() => {
    if (!latestImage) return;
    const img = new Image();
    img.onload = () => {
      latImgRef.current = img;
      setImagesLoaded((prev) => ({ ...prev, lat: true }));
    };
    img.src = `data:image/jpeg;base64,${latestImage}`;
  }, [latestImage]);

  // Draw composite whenever opacity, swap, or images change
  useEffect(() => {
    if (!imagesLoaded.ref || !imagesLoaded.lat) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const refImg = refImgRef.current;
    const latImg = latImgRef.current;
    const w = Math.max(refImg.width, latImg.width);
    const h = Math.max(refImg.height, latImg.height);
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, w, h);

    const redImage = swapped ? latImg : refImg;
    const blueImage = swapped ? refImg : latImg;
    const refOpacity = opacity / 100;
    const latOpacity = 1 - refOpacity;

    // Draw reference layer (red tinted)
    ctx.save();
    ctx.globalAlpha = swapped ? latOpacity : refOpacity;
    ctx.drawImage(redImage, 0, 0, w, h);
    // Apply red tint via overlay
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = "rgba(255, 80, 80, 0.4)";
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = "source-over";
    ctx.restore();

    // Draw latest layer (blue tinted) with screen blend
    ctx.save();
    ctx.globalAlpha = swapped ? refOpacity : latOpacity;
    ctx.globalCompositeOperation = "screen";
    ctx.drawImage(blueImage, 0, 0, w, h);
    // Apply blue tint
    ctx.globalCompositeOperation = "multiply";
    ctx.globalAlpha = 0.4;
    ctx.fillStyle = "rgba(80, 80, 255, 1)";
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }, [imagesLoaded, opacity, swapped]);

  const reset = useCallback(() => {
    setOpacity(50);
    setSwapped(false);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        <Spinner size={24} className="animate-spin mr-2" />
        <span className="text-sm">Loading full-resolution images...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-16 text-red-500 text-sm">
        Failed to load images: {error}
      </div>
    );
  }

  return (
    <div className="border-b border-slate-200" data-testid="overlay-viewer">
      {/* Toolbar */}
      <div className="px-3 py-2 flex items-center justify-between gap-4 bg-primary/5 border-b border-primary/10 flex-wrap">
        <div className="flex items-center gap-3">
          {/* Opacity slider */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-red-500 font-medium">
              {swapped ? "Latest" : "Reference"}
            </span>
            <input
              type="range"
              min={0}
              max={100}
              value={opacity}
              onChange={(e) => setOpacity(Number(e.target.value))}
              className="w-32 h-1.5 accent-primary cursor-pointer"
              data-testid="overlay-opacity-slider"
            />
            <span className="text-xs text-blue-500 font-medium">
              {swapped ? "Reference" : "Latest"}
            </span>
            <span className="text-xs text-slate-400 ml-1">
              {opacity}%
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setSwapped((s) => !s)}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors border border-slate-200"
            data-testid="overlay-swap-colors"
          >
            <ArrowsLeftRight size={13} />
            Swap Colors
          </button>
          <button
            onClick={reset}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors border border-slate-200"
            data-testid="overlay-reset"
          >
            <ArrowsCounterClockwise size={13} />
            Reset
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="px-3 py-1.5 flex items-center gap-4 text-xs bg-slate-50 border-b border-slate-100">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-red-400 inline-block" />
          {swapped ? "Latest" : "Reference (Older)"}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-blue-400 inline-block" />
          {swapped ? "Reference (Older)" : "Latest (Newer)"}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-purple-400 inline-block" />
          Overlap (unchanged)
        </span>
      </div>

      {/* Canvas */}
      <div className="bg-slate-100 flex items-center justify-center p-2" style={{ minHeight: 300 }}>
        {imagesLoaded.ref && imagesLoaded.lat ? (
          <canvas
            ref={canvasRef}
            className="max-w-full max-h-96 object-contain"
            data-testid="overlay-canvas"
          />
        ) : (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Spinner size={20} className="animate-spin mr-2" />
            <span className="text-sm">Preparing overlay...</span>
          </div>
        )}
      </div>
    </div>
  );
}
